import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface ConfirmDialogData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  tone?: 'primary' | 'warning' | 'danger';
}

@Component({
  selector: 'app-confirm-dialog',
  template: `
    <div class="min-w-[320px] max-w-[460px] overflow-hidden rounded-2xl bg-white text-slate-950 shadow-2xl">
      <div class="flex items-start gap-4 border-b border-slate-200 bg-slate-50 px-6 py-5">
        <span class="inline-flex h-10 w-10 flex-none items-center justify-center rounded-2xl" [ngClass]="iconClass">
          <i class="fa-solid" [ngClass]="iconName"></i>
        </span>
        <div class="min-w-0">
          <h2 class="m-0 text-lg font-black leading-6 tracking-normal text-slate-950">{{ data.title }}</h2>
          <p class="m-0 mt-1 text-xs font-bold uppercase tracking-[0.12em] text-slate-400">Confirmation</p>
        </div>
      </div>

      <div class="px-6 py-5">
        <p class="m-0 whitespace-pre-line text-[15px] font-medium leading-7 text-slate-600">{{ data.message }}</p>
      </div>

      <div class="flex items-center justify-end gap-3 bg-white px-6 pb-6">
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 px-4 text-sm font-black text-slate-700 transition hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
          (click)="onCancel()"
          cdkFocusInitial
        >
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button
          type="button"
          class="inline-flex min-h-10 items-center justify-center rounded-xl px-4 text-sm font-black text-white shadow-lg transition focus:outline-none focus:ring-2"
          [ngClass]="confirmButtonClass"
          (click)="onConfirm()"
        >
          {{ data.confirmText || 'Confirm' }}
        </button>
      </div>
    </div>
  `,
})
export class ConfirmDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {}

  get tone(): 'primary' | 'warning' | 'danger' {
    return this.data.tone || 'primary';
  }

  get iconName(): string {
    if (this.tone === 'danger') return 'fa-triangle-exclamation';
    if (this.tone === 'warning') return 'fa-circle-exclamation';
    return 'fa-circle-check';
  }

  get iconClass(): string {
    if (this.tone === 'danger') return 'bg-rose-100 text-rose-600';
    if (this.tone === 'warning') return 'bg-amber-100 text-amber-600';
    return 'bg-indigo-100 text-indigo-600';
  }

  get confirmButtonClass(): string {
    if (this.tone === 'danger') return 'bg-rose-600 shadow-rose-950/20 hover:bg-rose-500 focus:ring-rose-300';
    if (this.tone === 'warning') return 'bg-amber-500 shadow-amber-950/20 hover:bg-amber-400 focus:ring-amber-300';
    return 'bg-indigo-600 shadow-indigo-950/20 hover:bg-indigo-500 focus:ring-indigo-300';
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
